<template>
	<div
		class="hdl-nps question"
		:class="{
			'has-error': errorMap.size > 0
		}"
		@keydown="keydownHandler"
	>
		<div ref="rendered" v-html="question.renderedQuestion" />
		<div class="errors" :id="`errs-${question.uuid}`" v-if="errorMessages.length" aria-live="assertive">
			<ul>
				<li v-for="errorMessage of errorMessages" :key="errorMessage">
					{{ errorMessage }}
				</li>
			</ul>
		</div>
		<div
			class="container"
			ref="container"
		>
			<div class="choices">
				<div
					class="choice input-box-borders"
					v-for="choice in choices"
					:class="{
						'selected': choice === selectedChoice
					}"
					:key="choice"
					@click.stop="selectChoice(choice)"
					ref="choices"
					role="radio"
					:aria-checked="choice === selectedChoice"
					:aria-label="`${choice}`"
					:data-index=choice
					tabindex=0
				>{{ choice }}</div>
			</div>
			<div class="labels">
				<div class="left">Not at all likely</div>
				<div class="right">Very likely</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';
import { PublishedSurveyQuestionType, SurveyResponseErrorCode, SurveyResponseInputErrorType } from '@/common/interfaces';

import { IQuestionComponent } from './types';
import { ResponseGroup } from '../../responses';
import { useResponseStore } from '@/respondants/stores/responseStore';

@Component({
	emits: ['intentionalBlur']
})
export default class Nps extends Vue implements IQuestionComponent {
	@Prop({ required: true })
	question: PublishedSurveyQuestionType;

	response: string | null;

	errorMap: Map<SurveyResponseErrorCode, string> = new Map();

	selectedChoice: number = -1;

	numberInputTimeout: number | null = null;
	accumulatedNumbers: string = '';

	mounted(): void {
		this.$el.__vue__ = this;

		this.errorMap = new Map();

		if (this.responseGroup) {
			const existingResponse = this.responseGroup.getResponse(this.question.uuid);
			this.selectedChoice = (existingResponse && existingResponse.value)? existingResponse.value : -1;
		}

		this.accessorizeRendered();
	}

	unmounted(): void {
		if (this.numberInputTimeout !== null) {
			clearTimeout(this.numberInputTimeout);
		}
	}

	accessorizeRendered(): void {
		const rendered = this.$refs.rendered as HTMLDivElement | undefined;

		if (rendered === undefined) {
			return;
		}

		const label = rendered.querySelector(`label[for="${this.question.uuid}"]`);

		if (label !== null) {
			const labelTail = document.createElement('div');
			labelTail.classList.add('tail');
			label.appendChild(labelTail);

			if (this.question.required) {
				const sroRequiredIndicator = document.createElement('span');
				sroRequiredIndicator.classList.add('hdl-sr-only');
				sroRequiredIndicator.textContent = '(required)';
				labelTail.appendChild(sroRequiredIndicator);
				const requiredIndicator = document.createElement('div');
				requiredIndicator.setAttribute('aria-hidden', 'true');
				requiredIndicator.classList.add('required-indicator');
				requiredIndicator.textContent = '✱';
				labelTail.appendChild(requiredIndicator);
			}
		}
	}

	focusFrom(fromDirection: 'above' | 'below'): boolean {
		const choices = this.$refs.choices as Array<HTMLDivElement> | undefined;

		if (choices === undefined) {
			return true;
		}

		if (this.selectedChoice > -1 && this.selectedChoice < this.choices.length) {
			choices[this.selectedChoice].focus();
		} else if (choices.length > 0) {
			choices[0].focus();
		}

		return true;
	}

	keydownHandler(event: KeyboardEvent): void {
		const focusedIndex = this.focusedDomChoiceIndex();

		if (event.key === 'ArrowRight') {
			event.stopPropagation();
			event.preventDefault();
			const currentIndex = focusedIndex;
			if (currentIndex !== null && currentIndex < this.choices.length - 1) {
				const nextChoice = (this.$refs.choices as any)[currentIndex + 1];

				if (nextChoice) {
					nextChoice.focus();
				}
			}
		} else if (event.key == 'ArrowLeft') {
			event.stopPropagation();
			event.preventDefault();
			const currentIndex = focusedIndex;
			if (currentIndex !== null && currentIndex > 0) {
				const nextChoice = (this.$refs.choices as any)[currentIndex - 1];
				if (nextChoice) {
					nextChoice.focus();
				}
			}
		} else if (event.key === 'Enter' || event.key === ' ') {
			event.stopPropagation();
			event.preventDefault();

			if (focusedIndex !== null) {
				this.selectChoice(focusedIndex);
			}

			if (event.key === 'Enter') {
				this.$emit('intentionalBlur', this.$el, this.question, 'down');
			}
		} else if (event.key === 'ArrowUp') {
			event.stopPropagation();
			event.preventDefault();
			this.$emit('intentionalBlur', this.$el, this.question, 'up');
		} else if (event.key === 'ArrowDown') {
			event.stopPropagation();
			event.preventDefault();
			this.$emit('intentionalBlur', this.$el, this.question, 'down');
		}

		if (event.key >= '0' && event.key <= '9') {
			event.stopPropagation();
			event.preventDefault();

			this.accumulatedNumbers += event.key;

			if (this.accumulatedNumbers.length === 1) {
				// 99% of the time it will be a single digit, set it immediately but still listen
				//	for more numbers
				const parsed = parseInt(event.key);
				if (!isNaN(parsed) && parsed >= 0 && parsed < this.choices.length) {
					this.selectChoice(parsed);
				}
			}

			this.numberInputTimeout = setTimeout(() => {
				if (this.accumulatedNumbers.length === 1) {
					// It was already handled
					this.accumulatedNumbers = '';
					return;
				}
				let parsed = parseInt(this.accumulatedNumbers);
				this.accumulatedNumbers = '';

				if (isNaN(parsed)) {
					return;
				}

				if (parsed < 0 || parsed > this.choices.length - 1) {
					return;
				}

				this.selectChoice(parsed);
			}, 400);
		} else {
			this.accumulatedNumbers = '';
		}
	}

	selectChoice(selectedChoice: number): void {
		if (this.selectedChoice === selectedChoice) {
			this.selectedChoice = - 1;

			if (this.responseGroup) {
				this.responseGroup.setResponse(this.question.uuid, null, null, null);
			}
			return;
		}

		this.clearError(SurveyResponseErrorCode.MISSING_REQUIRED);

		this.selectedChoice = selectedChoice;

		if (this.responseGroup) {
			this.responseGroup.setResponse(this.question.uuid, null, this.selectedChoice, null);
		}
	}

	getResponse(): number | null {
		return (this.selectedChoice === -1)? null : this.selectedChoice;
	}

	hasResponse(): boolean {
		return this.selectedChoice !== -1;
	}

	clearError(errorCode: SurveyResponseErrorCode): void {
		this.errorMap.delete(errorCode);
	}

	markError(error: SurveyResponseInputErrorType): void {
		this.errorMap.set(error.code, error.error);
	}


	focusedDomChoiceIndex(): number | null {
		const container = this.$refs.container as HTMLDivElement | undefined;

		if (container === undefined) {
			return null;
		}

		if (document.activeElement && container.contains(document.activeElement)) {
			const index = document.activeElement.getAttribute('data-index');
			if (index === null) {
				return null;
			}

			const parsedIndex = parseInt(index);
			if (isNaN(parsedIndex)) {
				return null;
			}

			return parsedIndex;
		}

		return null;
	}

	get errorMessages(): Array<string> {
		return Array.from(this.errorMap.values());
	}

	get choices(): Array<number> {
		const stepSize = 1;
		const start = 0;
		const end = 10;

		const result: Array<number> = [];

		for (let i = start; i <= end; i+=stepSize) {
			result.push(i);
		}

		return result;
	}

	get responseGroup(): ResponseGroup | null {
		return this.responseStore.responseGroup;
	}

	get responseStore() {
		return useResponseStore();
	}
}
</script>

<style lang="scss">
.hdl-nps {
	&.has-error {
		label {
			@apply text-hdl-wc-red;
		}
	}

	.errors {
		@apply text-hdl-wc-red text-sm;

		ul {
			list-style: none;
			margin: 0;
			padding: 0;

			li {
				margin: 0;
				list-style-type: none;
			}
		}
	}

	.container {
		width: fit-content;

		.choices {
			@apply flex flex-row items-center mb-2 flex-wrap gap-y-2;

			.choice {
				@apply rounded text-center hover:bg-fill-hover cursor-pointer flex flex-row items-center justify-center select-none;
				width: 40px;
				height: 40px;

				transition: border-color 100ms linear;

				&:focus {
					outline: none;
					@apply bg-fill-hover;
				}

				&:not(:last-child) {
					@apply mr-2;
				}
			}
		}

		.labels {
			@apply flex flex-row items-center justify-between;

			.left,
			.right {
				@apply text-secondary;
			}
		}
	}
}
</style>
